// Login.js
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import supabase from "./supabaseClient";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Reusable styling classes matched exactly with the dashboard layouts
  const baseBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl text-slate-700 hover:bg-white/70 hover:text-slate-950";
  const activeBtnClass = "text-sm font-semibold transition-all duration-200 px-4 py-2 rounded-xl bg-white text-slate-900 shadow-sm";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    navigate("/admissionstoolkit");
  };

  const handleGoogleLogin = async () => {
    setMessage("");
    setLoading(true);

    const { error } = await supabase.auth.signIn(
      { provider: "google" },
      { redirectTo: window.location.origin + "/admissionstoolkit" }
    );

    setLoading(false);
    if (error) setMessage(error.message);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#edf4ff] text-slate-900 flex flex-col">

      {/* ===== BACKGROUND ===== */}

      {/* Base gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#ffffff,_#dbeafe_35%,_#c7dcff_65%,_#b5cbf4_100%)]" />

      {/* Moving shine */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="shine-overlay" />
      </div>

      {/* Floating glow orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="orb orb1" />
        <div className="orb orb2" />
        <div className="orb orb3" />
      </div>

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#1e3a5f_1px,transparent_1px),linear-gradient(to_bottom,#1e3a5f_1px,transparent_1px)] bg-[size:70px_70px]" />

      {/* Noise */}
      <div className="absolute inset-0 noise opacity-[0.035] mix-blend-overlay" />

      {/* ===== NAVBAR ===== */}
      <nav className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-7 flex items-center justify-between">

          {/* Logo */}
          <div
            onClick={() => navigate("/")}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#1e3a5f] text-white flex items-center justify-center font-black shadow-lg">
              C
            </div>

            <div className="font-bold tracking-tight text-lg">
              CollegeNavi
            </div>
          </div>

          {/* Nav */}
          <div className="flex items-center gap-3 md:gap-5">
            <Link
              to="/"
              className={location.pathname === "/" ? activeBtnClass : baseBtnClass}
            >
              Home
            </Link>
            <Link
              to="/register"
              className={location.pathname === "/register" ? activeBtnClass : baseBtnClass}
            >
              Join
            </Link>
          </div>
        </div>
      </nav>

      {/* ===== CENTERED LOGIN CARD COMPONENT ===== */}
      <main className="flex-1 flex items-center justify-center px-6 pt-36 pb-20 z-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="w-full max-w-md bg-white/65 backdrop-blur-2xl border border-white/60 rounded-[32px] p-8 md:p-10 shadow-[0_20px_80px_rgba(30,58,95,0.12)]"
        >
          <form onSubmit={handleSubmit}>
            <h2 className="text-3xl font-black text-slate-900 tracking-tight text-center mb-2">
              Log Back In
            </h2>
            <p className="text-center text-slate-600 text-sm font-medium tracking-wide mb-8">
              Sign in to continue your admissions path.
            </p>

            {/* Google Integration Trigger */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 text-sm font-semibold px-5 py-3 rounded-xl border border-black/20 bg-white/80 backdrop-blur-md shadow-[0_4px_12px_rgba(0,0,0,0.03)] hover:bg-white hover:border-black/30 transition-all duration-200 disabled:opacity-50"
            >
              <img
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                alt="Google"
                className="w-5 h-5"
              />
              Continue with Google
            </button>

            {/* Structural Custom Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="px-3 text-[10px] tracking-widest text-slate-400 font-bold uppercase">OR</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Email Input Field */}
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-white/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] text-sm text-slate-900 font-medium transition shadow-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {/* Password Input Field */}
            <div className="mb-6">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-white/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f] text-sm text-slate-900 font-medium transition shadow-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            {/* Primary Submit Action */}
            <button
              type="submit"
              disabled={loading}
              className="w-full text-sm font-semibold px-5 py-3 rounded-xl border border-black/30 bg-white/60 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:bg-white/80 hover:border-black/40 transition-all duration-200 disabled:opacity-50 text-slate-900"
            >
              {loading ? "Signing in..." : "Sign In to Account"}
            </button>

            {message && (
              <p className="mt-4 text-center text-sm font-semibold text-red-600 bg-red-50 p-2.5 rounded-xl border border-red-100">
                {message}
              </p>
            )}

            <div className="mt-6 text-center text-sm font-medium text-slate-600">
              Need an account?{" "}
              <Link to="/register" className="font-bold text-[#1e3a5f] hover:underline underline-offset-2">
                Register free
              </Link>
            </div>
          </form>
        </motion.div>
      </main>

      {/* ===== FOOTER ===== */}
      <div className="fixed bottom-5 right-5 z-50">
        <div className="flex items-center gap-3 bg-white/75 backdrop-blur-xl border border-white/70 rounded-2xl px-4 py-3 shadow-xl">
          <button
            onClick={() => navigate("/privacy")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Privacy
          </button>

          <div className="w-px h-4 bg-slate-300" />

          <button
            onClick={() => navigate("/public")}
            className="text-xs font-medium text-slate-600 hover:text-slate-900"
          >
            Policy
          </button>
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`

        .noise {
          background-image: url("https://www.transparenttextures.com/patterns/noise.png");
        }

        /* Glow orbs */
        .orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.45;
        }

        .orb1 {
          width: 420px;
          height: 420px;
          background: #60a5fa;
          top: 8%;
          left: -5%;
          animation: float1 16s ease-in-out infinite;
        }

        .orb2 {
          width: 340px;
          height: 340px;
          background: #93c5fd;
          bottom: -8%;
          right: -2%;
          animation: float2 20s ease-in-out infinite;
        }

        .orb3 {
          width: 260px;
          height: 260px;
          background: #dbeafe;
          top: 45%;
          left: 50%;
          animation: float3 18s ease-in-out infinite;
        }

        @keyframes float1 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(40px, -30px);
          }
        }

        @keyframes float2 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(-30px, 30px);
          }
        }

        @keyframes float3 {
          0%, 100% {
            transform: translate(0px, 0px);
          }
          50% {
            transform: translate(20px, -25px);
          }
        }

      `}</style>
    </div>
  );
}